<script setup lang="ts">
import { useDocumentVisibility, useIntersectionObserver, usePreferredReducedMotion } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    images: string[]
    title: string
    subtitle: string
    label?: string
    rotateEvery?: number
    fadeDuration?: number
  }>(),
  { label: '', rotateEvery: 13000, fadeDuration: 1500 }
)

const host = ref<HTMLElement>()

const activeIndex = ref(0)
const previousIndex = ref<number | null>(null)

const canRotate = computed(() => props.images.length > 1 && props.rotateEvery > 0)

const visibility = useDocumentVisibility()
const isInView = ref(true)
useIntersectionObserver(
  host,
  ([entry]) => {
    isInView.value = entry.isIntersecting
  },
  { threshold: 0.01 }
)
const isRunning = computed(() => visibility.value === 'visible' && isInView.value)

const reducedMotion = usePreferredReducedMotion()

const layerStyle = (index: number) => {
  const isActive = index === activeIndex.value
  const isPrevious = index === previousIndex.value

  return {
    opacity: isActive || isPrevious ? 1 : 0,
    zIndex: isActive ? 2 : isPrevious ? 1 : 0,
    transitionDuration: isActive ? `${props.fadeDuration}ms` : '0ms'
  }
}

let runId = 0
let timer: ReturnType<typeof setTimeout> | undefined

const sleep = (id: number, ms: number) =>
  new Promise<void>((resolve) => {
    const arm = () => {
      timer = setTimeout(
        () => {
          if (id !== runId) return
          if (isRunning.value) resolve()
          else arm()
        },
        isRunning.value ? ms : 200
      )
    }
    arm()
  })

const stop = () => {
  runId++
  clearTimeout(timer)
}

const start = async () => {
  const id = ++runId
  while (id === runId) {
    await sleep(id, props.rotateEvery)
    if (id !== runId) return
    previousIndex.value = activeIndex.value
    activeIndex.value = (activeIndex.value + 1) % props.images.length
  }
}

onMounted(() => {
  watch(
    [reducedMotion, canRotate],
    ([mode, rotates]) => {
      stop()
      if (mode === 'reduce' || !rotates) {
        activeIndex.value = 0
        previousIndex.value = null
        return
      }
      start()
    },
    { immediate: true }
  )
})

onBeforeUnmount(stop)
</script>

<template>
  <aside ref="host" class="h-full p-6">
    <div class="relative h-full overflow-hidden rounded-3xl">
      <img
        v-for="(image, index) in images"
        :key="image"
        :src="image"
        alt=""
        class="absolute inset-0 size-full object-cover transition-opacity ease-[cubic-bezier(0.45,0,0.55,1)]"
        :style="layerStyle(index)"
        :fetchpriority="index === 0 ? 'high' : 'low'"
        :loading="index === 0 ? 'eager' : 'lazy'"
      />

      <div class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-10 p-14 text-center">
        <div>
          <p
            v-if="label"
            class="mb-4 inline-flex items-center rounded-full border border-white/40 px-3 py-1 text-xs font-medium tracking-wide text-white/80"
          >
            {{ label }}
          </p>

          <p class="text-[28px] font-semibold leading-tight tracking-[-0.01em] text-white">
            {{ title }}
          </p>
          <p class="mx-auto mt-3 max-w-[30rem] text-base leading-relaxed text-white/90">
            {{ subtitle }}
          </p>
        </div>

        <slot name="animation" />
      </div>
    </div>
  </aside>
</template>
